/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * File Path: packages/ui/src/lib/icon.ts
 */

import {
	AddOutline,
	AddSharp,
	AlertCircleOutline,
	AnalyticsOutline,
	BagAddOutline,
	BookmarkOutline,
	BookOutline,
	BulbOutline,
	CalendarClearSharp,
	CallOutline,
	CaretForwardCircleOutline,
	ChatboxOutline,
	ChatbubbleEllipses,
	ChatbubbleEllipsesOutline,
	CheckmarkCircleOutline,
	CheckmarkSharp,
	ChevronBackOutline,
	ChevronDownOutline,
	ChevronForwardOutline,
	ChevronUpOutline,
	CloseOutline,
	DiamondOutline,
	DocumentOutline,
	DocumentTextOutline,
	EllipsisHorizontal,
	FlashOutline,
	FlowerOutline,
	FolderOutline,
	HomeOutline,
	InformationCircleOutline,
	ListOutline,
	MailOutline,
	MegaphoneOutline,
	MenuSharp,
	MicOutline,
	NewspaperOutline,
	NotificationsOutline,
	NotificationsSharp,
	PeopleOutline,
	PersonCircleOutline,
	PieChartOutline,
	PinOutline,
	PlayCircleOutline,
	PricetagOutline,
	RadioButtonOffSharp,
	RadioButtonOnSharp,
	RefreshOutline,
	SearchCircleOutline,
	SearchOutline,
	SettingsOutline,
	SparklesSharp,
	StatsChartOutline,
	StopCircleOutline,
	StopSharp,
	ThermometerOutline,
	WarningOutline
} from 'svelte-ionicons';
import type { Component } from 'svelte';

type IonValues =
	| 'radio-button-on'
	| 'radio-button-off'
	| 'home-outline'
	| 'document-text-outline'
	| 'document-outline'
	| 'list-outline'
	| 'book-outline'
	| 'mail-outline'
	| 'pie-chart-outline'
	| 'flower-outline'
	| 'notifications-outline'
	| 'analytics-outline'
	| 'bookmark-outline'
	| 'settings-outline'
	| 'search-outline'
	| 'search-circle-outline'
	| 'add-outline'
	| 'newspaper-outline'
	| 'people-outline'
	| 'stats-chart-outline'
	| 'mic-outline'
	| 'calendar-clear-sharp'
	| 'call-outline'
	| 'stop-outline'
	| 'flash-light-outline'
	| 'flash-outline'
	| 'menu-sharp'
	| 'checkmark-sharp'
	| 'check-circle-outline'
	| 'diamond-outline'
	| 'folder-outline'
	| 'megaphone-outline'
	| 'caret-forward-circle-outline'
	| 'ellipsis-horizontal'
	| 'chevron-left'
	| 'chevron-right'
	| 'chevron-down'
	| 'chevron-up'
	| 'notifications-sharp'
	| 'information-circle-outline'
	| 'close-outline'
	| 'pin-outline'
	| 'chatbox-outline'
	| 'alert-circle-outline'
	| 'warning-outline'
	| 'refresh-outline'
	| 'add-sharp'
	| 'thermometer-outline'
	| 'chatbubble-ellipses-outline'
	| 'chatbubble-ellipses'
	| 'user-circle-outline'
	| 'pricetag-outline'
	| 'bag-add-outline'
	| 'play-circle-outline'
	| 'stop-circle-outline'
	| 'bulb-outline';

// Icon map with imported components
export const iconMap: Record<IonValues, Component> = {
	'radio-button-on': RadioButtonOnSharp,
	'radio-button-off': RadioButtonOffSharp,
	'home-outline': HomeOutline,
	'document-text-outline': DocumentTextOutline,
	'document-outline': DocumentOutline,
	'list-outline': ListOutline,
	'book-outline': BookOutline,
	'mail-outline': MailOutline,
	'pie-chart-outline': PieChartOutline,
	'flower-outline': FlowerOutline,
	'notifications-outline': NotificationsOutline,
	'analytics-outline': AnalyticsOutline,
	'bookmark-outline': BookmarkOutline,
	'settings-outline': SettingsOutline,
	'search-outline': SearchOutline,
	'search-circle-outline': SearchCircleOutline,
	'add-outline': AddOutline,
	'newspaper-outline': NewspaperOutline,
	'people-outline': PeopleOutline,
	'stats-chart-outline': StatsChartOutline,
	'mic-outline': MicOutline,
	'calendar-clear-sharp': CalendarClearSharp,
	'call-outline': CallOutline,
	'stop-outline': StopSharp,
	'flash-light-outline': SparklesSharp,
	'flash-outline': FlashOutline,
	'menu-sharp': MenuSharp,
	'checkmark-sharp': CheckmarkSharp,
	'check-circle-outline': CheckmarkCircleOutline,
	'diamond-outline': DiamondOutline,
	'folder-outline': FolderOutline,
	'megaphone-outline': MegaphoneOutline,
	'caret-forward-circle-outline': CaretForwardCircleOutline,
	'ellipsis-horizontal': EllipsisHorizontal,
	'chevron-left': ChevronBackOutline,
	'chevron-right': ChevronForwardOutline,
	'chevron-down': ChevronDownOutline,
	'chevron-up': ChevronUpOutline,
	'notifications-sharp': NotificationsSharp,
	'information-circle-outline': InformationCircleOutline,
	'close-outline': CloseOutline,
	'pin-outline': PinOutline,
	'chatbox-outline': ChatboxOutline,
	'alert-circle-outline': AlertCircleOutline,
	'warning-outline': WarningOutline,
	'refresh-outline': RefreshOutline,
	'add-sharp': AddSharp,
	'thermometer-outline': ThermometerOutline,
	'chatbubble-ellipses-outline': ChatbubbleEllipsesOutline,
	'chatbubble-ellipses': ChatbubbleEllipses,
	'user-circle-outline': PersonCircleOutline,
	'pricetag-outline': PricetagOutline,
	'bag-add-outline': BagAddOutline,
	'play-circle-outline': PlayCircleOutline,
	'stop-circle-outline': StopCircleOutline,
	'bulb-outline': BulbOutline
};

import { type IconValues } from './generated/icons';

export type UnionIconValues = IonValues | IconValues;
